import { useMemo } from 'react';

import { paths } from 'src/routes/paths';

import SvgColor from 'src/components/svg-color';

import { useAuthContext } from '../../auth/hooks';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),

  // new icons from here on down
  profile: icon('profile'),
  chart: icon('chart'),
  home: icon('home'),
  list: icon('list'),
  matching: icon('matching'),
  usage: icon('usage'),
  barGraph: icon('barGraph'),
  funnel: icon('funnel'),
  arrowRight: icon('arrowRight'),
  editNote: icon('editNote'),
  peopleOutline: icon('peopleOutline'),
};

const practiceFlowEnabled = import.meta.env.VITE_PRACTICE_FLOW === 'ON';
const providerFlowEnabled = import.meta.env.VITE_PROVIDER_FLOW === 'ON';
const adminFlowEnabled = import.meta.env.VITE_ADMIN_FLOW === 'ON';
// const clientIntakeFlowEnabled = import.meta.env.VITE_CLIENT_INTAKE_FLOW === 'ON';
// const clientReferralFlowEnabled = import.meta.env.VITE_CLIENT_REFERRAL === 'ON';
// const groupFlowEnabled = import.meta.env.VITE_GROUP_FLOW === 'ON';
// const practiceRosterFlowEnabled = import.meta.env.VITE_PRACTICE_ROSTER_FLOW === 'ON';
// const clientMatchingFlowEnabled = import.meta.env.VITE_CLIENT_MATCHING_FLOW === 'ON';

function buildAdminSection() {

  return {
    subheader: 'Admins',
    roles: ['Practice_Owner', 'Full_Admin'],
    items: [
      {
        title: 'Clients',
        path: paths.dashboard.admin.clients,
        icon: ICONS.funnel,
      },
      {
        title: 'Clients - Other',
        path: paths.dashboard.admin.specialCaseClients,
        icon: ICONS.funnel,
      },
      {
        title: 'Referral Requests',
        path: paths.dashboard.admin.referralRequests,
        icon: ICONS.editNote,
      },
      {
        title: 'Matching by Provider',
        path: paths.dashboard.admin.matchingByProvider,
        icon: ICONS.matching,
      },
      {
        title: 'Matching by Client',
        path: paths.dashboard.admin.matchingByClient,
        icon: ICONS.matching,
      },
      {
        title: 'Enrollments',
        path: paths.dashboard.admin.adminEnrollments,
        icon: ICONS.chart,
      },
      // {
      //   title: 'Provider Roster',
      //   path: paths.dashboard.admin.providerRoster,
      //   icon: ICONS.list,
      // },
      // {
      //   title: 'Practice Roster',
      //   path: paths.dashboard.admin.practiceRoster,
      //   icon: ICONS.list,
      // },
      // {
      //   title: 'Group Directory',
      //   path: paths.dashboard.admin.groupsRoster,
      //   icon: ICONS.list,
      // }
    ],
  };
}

function buildProviderSection(user: Record<string, any> | null) {
  const providerSection = {
    subheader: 'Providers',
    roles: ['Provider'],
    items: [
      {
        title: 'Profile',
        path: paths.dashboard.provider.providerProfile,
        icon: ICONS.profile,
      },
      {
        title: 'Caseload',
        path: paths.dashboard.provider.providerCaseload,
        icon: ICONS.peopleOutline,
        countKey: 'pendingConsultations',
      },
      {
        title: 'Enrollments',
        path: paths.dashboard.provider.enrollments,
        icon: ICONS.chart
      }
    ],
  };


  const qualitySection = {
    title: 'Quality',
    icon: ICONS.dashboard,
    children: [
      {
        title: 'Missing Notes',
        path: paths.dashboard.analyticsMissingNotes,
      },
      {
        title: 'Treatment Plans',
        path: paths.dashboard.analyticsTreatmentPlans,
      },
    ],
  };

  // @ts-ignore
  providerSection.items.push(qualitySection);

  if (user && user.roles && user.roles.includes('Supervisor')) {
    qualitySection.children.push({
      title: 'Supervisors',
      path: paths.dashboard.analyticsSupervisors,
    });
  }
  return providerSection;
}

function buildPracticeSection() {
  return {
    subheader: 'Practice',
    roles: ['Practice_Owner'],
    items: [
      {
        title: 'Profile',
        path: paths.dashboard.practice.practiceProfile,
        icon: ICONS.home,
      },
      {
        title: 'Provider Roster',
        path: paths.dashboard.practice.practiceProviderRoster,
        icon: ICONS.list,
      },
      {
        title: 'Client List',
        path: paths.dashboard.practice.practiceClientList,
        icon: ICONS.list,
      },
    ],
  };
}

export function useNavData() {
  const { user } = useAuthContext();

  return useMemo(() => {
    const sections = [
      {
        roles: ['practice_manager', 'supervisor', 'biller', 'scheduler', 'clinician_admin', 'clinician_full', 'clinician', 'clinician_self',],
        items: [
          {
            title: 'Home',
            path: paths.dashboard.root,
            icon: ICONS.home,
          },
        ],
      },
    ];

    if (adminFlowEnabled) {
      sections.push(buildAdminSection());
    }
    if (providerFlowEnabled) {
      sections.push(buildProviderSection(user));
    }
    if (practiceFlowEnabled) {
      sections.push(buildPracticeSection());
    }
    return sections;
  }, [user]);
}
